import { api as apiClient } from './apiClient';

export interface Location {
  id: string;
  userId: string;
  unitCode: string;
  type?: string;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  };
  isActive?: boolean;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    jobTitle?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateLocationDto {
  unitCode: string;
  type?: string;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  };
  userId?: string;
}

export interface LocationStats {
  totalLocations: number;
  byCountry: Record<string, number>;
}

class LocationService {
  /**
   * Get all locations accessible by current user
   */
  async getLocations(): Promise<Location[]> {
    const response = await apiClient.get<Location[]>('/locations');
    return response.data || [];
  }

  /**
   * Get locations for a specific user
   */
  async getLocationsByUserId(userId: string): Promise<Location[]> {
    const response = await apiClient.get<Location[]>(`/locations/user/${userId}`);
    return response.data || [];
  }

  /**
   * Get a single location by ID
   */
  async getLocationById(id: string): Promise<Location> {
    const response = await apiClient.get<Location>(`/locations/${id}`);
    if (!response.data) {
      throw new Error('Facility not found');
    }
    return response.data;
  }

  /**
   * Create a new location
   */
  async createLocation(data: CreateLocationDto): Promise<Location> {
    const response = await apiClient.post<Location>('/locations', data, {
      showSuccessToast: true,
      successMessage: 'Facility created successfully',
    });
    if (!response.data) {
      throw new Error('Failed to create facility');
    }
    return response.data;
  }

  /**
   * Update a location
   */
  async updateLocation(id: string, data: Partial<CreateLocationDto>): Promise<Location> {
    const response = await apiClient.patch<Location>(`/locations/${id}`, data, {
      showSuccessToast: true,
      successMessage: 'Facility updated successfully',
    });
    if (!response.data) {
      throw new Error('Failed to update facility');
    }
    return response.data;
  }

  /**
   * Delete a location (soft delete)
   */
  async deleteLocation(id: string): Promise<void> {
    await apiClient.delete(`/locations/${id}`, {
      showSuccessToast: true,
      successMessage: 'Facility deleted successfully',
    });
  }

  /**
   * Get location statistics
   */
  async getLocationStats(): Promise<LocationStats> {
    const response = await apiClient.get<LocationStats>('/locations/stats');
    return response.data || {
      totalLocations: 0,
      byCountry: {},
    };
  }
}

export default new LocationService();
